import '@srgssr/srgletterbox-web/dist/letterbox.css';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import SRGLetterbox from '@srgssr/srgletterbox-web';
import { logger } from '../Debug/DebugLogging';

// empty string means no subtitles

export const useLetterbox = ({
  urn,
  ilHost = 'il.srgssr.ch',
  language = 'de',
  seekPosition = 0,
  autoPlay = false,
  playerFocus = false,
  standalone = false,
  textTrackSizeLevel,
  hideSegments = false,
  hideShareOptions = false,
  showTitle = false,
  showContinuousPlayback = false,
  showPlayIcon = false,
  recommendations = true,
  endScreenPlaylist = undefined,
  textTrackDefaultLanguage = '',
  textTrackForcedLanguage,
  suppressTracking = false,
  calculateTimestampOffsetForEachSegment = false
}) => {
  const playerRef = useRef();
  const [player, setPlayer] = useState();
  const playerUrnRef = useRef();
  const id = useId().replace(/:/g, '__');
  const playerId = useMemo(() => `letterbox-player${id}`, [id]);
  const playerOptions = useMemo(() => {
    const options = {
      container: `#${playerId}`,
      ilHost,
      language,
      recommendations,
      endScreenPlaylist,
      playerFocus: playerFocus ? {
        preventScroll: true
      } : undefined,
      components: {
        subdivisions: !hideSegments,
        sharing: !hideShareOptions,
        title: showTitle,
        playButton: showPlayIcon
      },
      autoplay: autoPlay,
      continuousPlayback: showContinuousPlayback ? {
        default: true,
        storage: true
      } : false,
      textTrackLanguage: {
        default: textTrackForcedLanguage ?? textTrackDefaultLanguage,
        // subtitle changes by user are stored by the player, but not when the page was loaded with a forced subtitle language
        storage: !textTrackForcedLanguage
      },
      textTrackSizeLevel: {
        default: textTrackSizeLevel ?? 1,
        storage: !Number.isInteger(textTrackSizeLevel) // do not store changes if textTrackSizeLevel is enforced
      },
      audioTrackLanguage: {
        default: '',
        storage: true
      },
      tagCommanderScriptURL: suppressTracking ? '' : 'https://colibri-js.akamaized.net/penguin/tc_SRGGD_11.js',
      userReport: {
        errors: []
      },
      calculateTimestampOffsetForEachSegment
    };
    return options;
  }, [playerId, ilHost, language, recommendations, endScreenPlaylist, playerFocus, hideSegments, hideShareOptions, showTitle, showPlayIcon, autoPlay, showContinuousPlayback, textTrackForcedLanguage, textTrackDefaultLanguage, textTrackSizeLevel, suppressTracking, calculateTimestampOffsetForEachSegment]);
  useEffect(() => {
    if (playerRef.current && urn !== playerUrnRef.current) {
      try {
        const mediaComposition = playerRef.current?.getMediaComposition();
        const subdivisions = mediaComposition?.getSubdivisions();
        const isSubdivisionSwitch = subdivisions?.find(subdivision => subdivision.urn === urn);
        if (isSubdivisionSwitch) {
          playerUrnRef.current = urn;
          return;
        }
      } catch (error) {
        logger.error(error);
      }
    }
    if (!playerRef.current) {
      playerRef.current = new SRGLetterbox(playerOptions);
    } else {
      playerRef.current.updateConfiguration(playerOptions);

      // When the endscreen playlist is updated, the player needs to fetch the latest media.
      // Unfortunately this is not done automatically, so we need to trigger it manually after a short delay.
      setTimeout(() => {
        playerRef.current?.player.endScreen.loadMediaSet();
      }, 100);
    }
    if (playerUrnRef.current !== urn) {
      playerRef.current.loadUrn(urn, {
        pendingSeek: seekPosition,
        standalone
      });
      playerUrnRef.current = urn;
    }
    setPlayer(playerRef.current);
  }, [playerId, urn, autoPlay, seekPosition, playerOptions, standalone]);
  useEffect(() => () => {
    playerRef.current?.dispose();
  }, []);
  return {
    id: playerId,
    player
  };
};